import React, { Fragment, useState, useEffect, FC } from 'react';

import { getAccOrigin } from '@adsk/acs-sc-web-platform-api';
import { Box } from '@adsk/alloy-react-box';
import EmptyState from '@adsk/alloy-react-empty-state';
import Illustration from '@adsk/alloy-react-illustration';
import { SimpleModal } from '@adsk/alloy-react-modal';

import { useTranslation } from '../i18n';
import AutomaticAboutBoxContent from './AutomaticAboutBoxContent';
import { CURRENT_YEAR_PLACEHOLDER, INTRODUCTION } from '../consts';
import { Collection, LicenseDataType } from '../helpers/types';

type AboutBoxModalProps = {
  show: boolean;
  onClose: () => void;
  licenseData?: LicenseDataType | Collection[];
};

const AboutBoxModal: FC<AboutBoxModalProps> = ({
  show,
  licenseData,
  ...props
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<LicenseDataType | Collection[] | undefined>(
    licenseData,
  );
  const [error, setError] = useState(false);
  const linkRegex = /<a.*?\/a>/g;
  useEffect(() => {
    let cancel = false;

    if (show && !licenseData) {
      fetch(`${getAccOrigin()}/assets/license-manager/licenseCollections.json`)
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            throw new Error('License failed to load.');
          }
        })
        .then((newData) => {
          if (!cancel) {
            setData(newData);
          }
        })
        .catch(() => {
          if (!cancel) {
            setError(true);
          }
        });
    }

    return () => {
      cancel = true;
    };
  }, [show, licenseData]);

  return (
    <SimpleModal
      title={t('TRADEMARKS_CREDITS_AND_ATTRIBUTIONS')}
      modalBodyProps={{
        style: {
          overflow: 'hidden',
          overflowY: 'auto',
          ...(error && {
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
          }),
        },
      }}
      show={show}
      {...props}
    >
      {data &&
        INTRODUCTION.map((line) => {
          const textArr = line.text
            .replace(
              CURRENT_YEAR_PLACEHOLDER,
              new Date().getFullYear().toString(),
            )
            .replace(linkRegex, '-')
            .split('-');
          const links =
            line.text.match(linkRegex) &&
            line.text.match(linkRegex)?.map((link) => ({
              href: link.match(/href="(.*)">/)?.[1],
              text: link.match(/>(.*)</)?.[1],
            }));

          return (
            <Box key={line.text} margin="10px 0 4px">
              {textArr.map((text, i) => (
                <Fragment key={text}>
                  <span>{text}</span>
                  {links && links[i] && (
                    <a href={links[i].href}>{links[i].text}</a>
                  )}
                </Fragment>
              ))}
            </Box>
          );
        })}
      <AutomaticAboutBoxContent collections={data as Collection[]} />
      {!data && error && (
        <EmptyState
          title={t('ERROR_LOADING_LICENSES')}
          illustrationType={Illustration.TYPES.FOLDER_EMPTY_GREY}
          description={t('ERROR_LOADING_THIRD_PARTY')}
          style={{ marginTop: -12 }} // Visually center error block
        />
      )}
    </SimpleModal>
  );
};

AboutBoxModal.displayName = 'AboutBoxModal';

export default AboutBoxModal;
